import { Link } from 'react-router-dom';
import type { ThreadDto } from '@aiai/shared';
import { formatRelative, snippet } from '../../lib/format';
import { personLabel, useViewerEmail } from '../../lib/people';
import { UNTITLED_THREAD } from './ThreadHeader';

/**
 * One thread in the list: title, a one-line excerpt of "Where we are", the
 * meta line (who started it, posts, last activity) and the ledger counts.
 * `N suggested` is the only thing that asks for attention, so it alone wears
 * the signal colour — and only while N > 0.
 */
export function ThreadListRow({ thread, to }: { thread: ThreadDto; to: string }) {
  const viewerEmail = useViewerEmail();
  const { suggested, filed } = thread.proposalCounts;
  const excerpt = thread.summary ? snippet(thread.summary, 140) : '';
  const meta = [
    thread.startedByEmail ? `by ${personLabel(thread.startedByEmail, viewerEmail)}` : null,
    `${thread.messageCount} post${thread.messageCount === 1 ? '' : 's'}`,
    formatRelative(thread.lastMessageAt),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <li className="flex items-start justify-between gap-4 px-4 py-3 transition-colors hover:bg-surface-2">
      <div className="min-w-0 flex-1">
        <Link
          to={to}
          className="block truncate text-sm font-semibold text-ink hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-signal/60"
        >
          {thread.title?.trim() || UNTITLED_THREAD}
        </Link>
        {excerpt && <p className="mt-0.5 truncate text-xs text-ink-muted">{excerpt}</p>}
        <p className="mt-0.5 text-2xs text-ink-faint">{meta}</p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1 text-2xs sm:flex-row sm:items-center sm:gap-2">
        <span className={suggested > 0 ? 'font-semibold text-signal' : 'text-ink-faint'}>
          {suggested} suggested
        </span>
        <span className="text-ink-faint">{filed} filed</span>
      </div>
    </li>
  );
}
